import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ApiError, api, newIdempotencyKey } from "@/lib/api";
import { money, transitCopy } from "@/lib/format";
import type { Address, CheckoutResult, Parcel, Quote, RateOption, WalletSummary } from "@/lib/types";
import { Alert, Badge, Button, Card, Field, PageHeader, Spinner, cx } from "@/components/ui";
import { AddressForm, EMPTY_ADDRESS } from "@/components/AddressForm";
import { ParcelFields, describedParcel, isProhibited } from "@/components/ParcelFields";
import {
  EMPTY_SCHEDULE,
  SchedulePicker,
  formatDate,
  formatDeliveryEstimate,
  type ScheduleSelection,
} from "@/components/SchedulePicker";
import { EMPTY_PARCEL } from "@/lib/parcelCatalog";
import {
  focusFirstMissing,
  missingAddressFields,
  summariseMissing,
  toFieldErrors,
} from "@/lib/required";
import { BookingSummary } from "@/components/BookingSummary";
import { scrollToTop } from "@/components/ScrollToTop";
import { useAuth } from "@/state/auth";
import { P } from "@/lib/permissions";

const STEPS = ["Addresses", "Parcels", "Rates", "Confirm"] as const;

export function Book() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { account, user, can } = useAuth();

  const [step, setStep] = useState(0);
  const [collection, setCollection] = useState<Address>({ ...EMPTY_ADDRESS });
  const [delivery, setDelivery] = useState<Address>({ ...EMPTY_ADDRESS });
  const [parcels, setParcels] = useState<Parcel[]>([{ ...EMPTY_PARCEL }]);
  const [categoryIds, setCategoryIds] = useState<(string | null)[]>([null]);
  const [reference, setReference] = useState("");
  const [declaredValue, setDeclaredValue] = useState("");
  const [insured, setInsured] = useState(false);
  const [schedule, setSchedule] = useState<ScheduleSelection>(EMPTY_SCHEDULE);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [selected, setSelected] = useState<RateOption | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Prefill happens once, and never over something a person has typed.
  const [prefilled, setPrefilled] = useState(false);
  const [collectionTouched, setCollectionTouched] = useState(false);
  const [message, setMessage] = useState("");

  // One key per booking attempt. It survives retries within this session but
  // changes when the customer starts a genuinely new booking.
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const { data: saved } = useQuery({
    queryKey: ["addresses"],
    queryFn: () => api.get<{ data: Address[] }>("/v1/addresses"),
  });

  /**
   * The wallet, so the summary can say what is actually due now rather than
   * only what the shipment costs. A customer without billing permission does
   * not get this call, and the summary simply shows the total instead.
   */
  const { data: walletData } = useQuery({
    queryKey: ["wallet"],
    queryFn: () => api.get<{ wallet: WalletSummary }>("/v1/wallet"),
    enabled: can(P.viewBilling),
  });
  const wallet = walletData?.wallet;

  /**
   * Fill in our own side of the shipment.
   *
   * A signed-in customer should never type their own collection address: the
   * account already knows it. The server resolves it too, from the account
   * rather than the request, but it has to be on screen for the person to see
   * what they are about to book and to change it if this one is going out from
   * somewhere else.
   *
   * Only while the form is still untouched. Once somebody has typed into it,
   * replacing what they wrote because a query resolved late would be worse than
   * not prefilling at all.
   */
  useEffect(() => {
    if (prefilled || collectionTouched) return;
    const addresses = saved?.data ?? [];
    if (addresses.length === 0) return;
    const fallback = addresses.find((a) => a.is_default) ?? addresses[0];
    setCollection({
      ...fallback,
      // Whoever is signed in is the person a driver will ring about this
      // collection, where the saved address may carry a stale name.
      contact_name: fallback.contact_name || [user?.first_name, user?.last_name].filter(Boolean).join(" "),
      phone: fallback.phone || account?.phone || "",
    });
    setPrefilled(true);
  }, [saved, prefilled, collectionTouched, user, account]);

  const quoteMutation = useMutation({
    mutationFn: () =>
      api.post<Quote>("/v1/quotes", {
        collection,
        delivery,
        parcels: parcels.map((parcel, index) => describedParcel(parcel, categoryIds[index] ?? null)),
        declared_value_cents: declaredValue ? Math.round(Number(declaredValue) * 100) : 0,
        insurance: insured,
      }),
    onSuccess: (result) => {
      setQuote(result);
      setSelected(result.options.find((option) => option.badge === "recommended") ?? result.options[0] ?? null);
      setFieldErrors({});
      setMessage("");
      setStep(2);
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fields);
        setMessage(Object.keys(error.fields).length ? "" : error.message);
        // Send the customer back to the step that holds the broken field.
        if (Object.keys(error.fields).some((key) => key.startsWith("collection") || key.startsWith("delivery"))) {
          setStep(0);
        } else if (Object.keys(error.fields).some((key) => key.startsWith("parcel"))) {
          setStep(1);
        }
      } else {
        setMessage("Could not fetch rates. Try again.");
      }
    },
  });

  /**
   * The booking goes through checkout, not straight at /v1/shipments.
   *
   * A wallet that covers the price pays it and we land on the shipment, which
   * is what used to happen. A wallet that does not is no longer a refusal on
   * the last step of a form the customer has already filled in: the API hands
   * back a gateway page for the shortfall, and the webhook books the shipment
   * once the money is real. Either way the customer presses one button.
   */
  const bookMutation = useMutation({
    mutationFn: () =>
      api.post<CheckoutResult>(
        "/v1/shipments/checkout",
        {
          quote_id: quote?.id,
          option_id: selected?.id,
          reference: reference || undefined,
          collection_date: schedule.collectionDate || undefined,
          requested_collection_after: schedule.collectionAfter || undefined,
          requested_collection_before: schedule.collectionBefore || undefined,
          requested_delivery_after: schedule.deliveryAfter || undefined,
          requested_delivery_before: schedule.deliveryBefore || undefined,
          // The quote was priced from the delivery address alone, so the person
          // receiving the parcel is sent now. The API accepts contact fields
          // only: the address itself is what the price was based on and cannot
          // be changed here.
          delivery_contact: {
            contact_name: delivery.contact_name || undefined,
            phone: delivery.phone || undefined,
            email: delivery.email || undefined,
          },
        },
        idempotencyKey,
      ),
    onSuccess: (result) => {
      if (result.paid_from === "card" && result.redirect_url) {
        // Leaving the app for the gateway. Nothing is booked yet; the page the
        // customer comes back to does the waiting.
        window.location.assign(result.redirect_url);
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["shipments"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      if (result.shipment) {
        navigate(`/app/shipments/${result.shipment.id}?booked=1`);
      }
    },
    onError: (error) => {
      if (error instanceof ApiError && Object.keys(error.fields).length) {
        setFieldErrors(error.fields);
        setMessage(error.message);
      } else {
        setMessage(error instanceof ApiError ? error.message : "Booking failed. Try again.");
      }
    },
  });

  // Advancing a step changes what is on the screen without changing the route,
  // so ScrollToTop never fires: without this, pressing Continue leaves you
  // looking at the middle of the next step.
  useEffect(() => scrollToTop(), [step]);

  const totalWeight = useMemo(() => parcels.reduce((sum, parcel) => sum + Number(parcel.weight_g || 0), 0), [parcels]);

  /**
   * What a rate needs, and what a booking needs, are different sets.
   *
   * Pricing a route needs a place; it does not need to know who lives there. So
   * the addresses step asks only for what makes the route priceable, and the
   * recipient's name and number are collected at the confirm step, where they
   * start to matter. This mirrors the API, which validates the two separately.
   */
  const quoteProblems = useMemo(
    () => [
      ...missingAddressFields(collection, "collection", "quote"),
      ...missingAddressFields(delivery, "delivery", "quote"),
    ],
    [collection, delivery],
  );
  const bookingProblems = useMemo(
    () => [
      ...missingAddressFields(collection, "collection", "booking"),
      ...missingAddressFields(delivery, "delivery", "booking"),
    ],
    [collection, delivery],
  );
  const canQuote = quoteProblems.length === 0;
  const canBook = bookingProblems.length === 0;

  // What this press will actually do. The API makes the same comparison when
  // it decides between spending the wallet and opening a gateway session; this
  // only mirrors it so the button can be honest about which one is coming.
  const dueNow = Math.max((selected?.price_cents ?? 0) - Math.max(wallet?.available_cents ?? 0, 0), 0);
  const payingByCard = Boolean(wallet) && dueNow > 0;

  /**
   * Acting with something missing.
   *
   * The button stays enabled on purpose. A disabled button says no without
   * saying why, and the field at fault is usually scrolled off the screen; this
   * marks every missing field, says what is outstanding, and puts the cursor in
   * the first one.
   */
  function guard(problems: typeof quoteProblems, run: () => void) {
    if (problems.length > 0) {
      setFieldErrors(toFieldErrors(problems));
      focusFirstMissing(problems);
      return;
    }
    setFieldErrors({});
    run();
  }

  function updateParcel(index: number, patch: Partial<Parcel>) {
    setParcels((prev) => prev.map((parcel, i) => (i === index ? { ...parcel, ...patch } : parcel)));
  }

  function addParcel() {
    setParcels((prev) => [...prev, { ...EMPTY_PARCEL }]);
    setCategoryIds((prev) => [...prev, null]);
  }

  function removeParcel(index: number) {
    setParcels((prev) => prev.filter((_, i) => i !== index));
    setCategoryIds((prev) => prev.filter((_, i) => i !== index));
  }

  const hasProhibited = categoryIds.some((id) => isProhibited(id));

  return (
    <>
      <PageHeader title="Book a shipment" subtitle="Choose a service and we will collect it." />

      <ol className="mb-6 flex flex-wrap gap-2">
        {STEPS.map((label, index) => (
          <li key={label}>
            <button
              type="button"
              disabled={index > step}
              onClick={() => setStep(index)}
              className={cx(
                "rounded-lg px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40",
                index === step ? "bg-ink-900 text-white" : "bg-white text-ink-600",
              )}
            >
              {index + 1}. {label}
            </button>
          </li>
        ))}
      </ol>

      {message && (
        <div className="mb-4">
          <Alert>{message}</Alert>
        </div>
      )}

      {step === 0 && (
        <div className="space-y-5">
          {!!saved?.data.length && (
            <Card>
              <p className="mb-3 text-sm font-medium">Use a saved address</p>
              <div className="flex flex-wrap gap-2">
                {saved.data.map((address) => (
                  <div key={address.id} className="flex items-center gap-1 rounded-xl border border-ink-200 px-3 py-2 text-sm">
                    <span className="mr-1">{address.label || address.suburb}</span>
                    <button
                      type="button"
                      className="text-xs font-medium text-brand"
                      onClick={() => {
                        setCollectionTouched(true);
                        setCollection({ ...address });
                      }}
                    >
                      From
                    </button>
                    <button type="button" className="text-xs font-medium text-brand" onClick={() => setDelivery({ ...address })}>
                      To
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Side by side from lg up: collection and delivery are independent
              forms, so there is nothing gained by making the customer scroll
              past one to reach the other on anything wider than a tablet. */}
          <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
            <Card>
              <h2 className="mb-4 text-lg font-semibold tracking-tight">Collect from</h2>
              <AddressForm
                prefix="collection"
                value={collection}
                onChange={(next) => {
                  setCollectionTouched(true);
                  setCollection(next);
                }}
                errors={fieldErrors}
              />
            </Card>

            <Card>
              <h2 className="mb-4 text-lg font-semibold tracking-tight">Deliver to</h2>
              <AddressForm prefix="delivery" value={delivery} onChange={setDelivery} errors={fieldErrors} />
            </Card>
          </div>

          <div className="flex justify-end">
            <div className="w-full sm:w-auto sm:text-right">
              {!canQuote && <p className="mb-2 text-sm text-ink-500">{summariseMissing(quoteProblems)}</p>}
              {/* One way forward, not a choice between two. Booking always
                  needs the parcel described, so describing it is simply the
                  next step rather than something to opt into beside a button
                  that skips it. Pricing without a parcel still exists, on the
                  rate calculator, where nothing is being booked. */}
              <Button
                className="w-full sm:w-auto"
                onClick={() => guard(quoteProblems, () => setStep(1))}
              >
                Continue to parcels
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5">
          {parcels.map((parcel, index) => (
            <Card key={index}>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold tracking-tight">Parcel {index + 1}</h2>
                {parcels.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => removeParcel(index)}>
                    Remove
                  </Button>
                )}
              </div>
              <ParcelFields
                parcel={parcel}
                categoryId={categoryIds[index] ?? null}
                onChangeParcel={(patch) => updateParcel(index, patch)}
                onChangeCategoryId={(next) => setCategoryIds((prev) => prev.map((c, i) => (i === index ? next : c)))}
                errors={fieldErrors}
                prefix={`parcels.${index}`}
              />
            </Card>
          ))}

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" onClick={addParcel}>
              Add another parcel
            </Button>
            <p className="text-sm text-ink-400">
              {parcels.length} parcel{parcels.length === 1 ? "" : "s"}, {(totalWeight / 1000).toFixed(2)} kg total
            </p>
          </div>

          <Card>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Your reference (optional)"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Order 1043"
              />
              <Field
                label="Declared value (R)"
                type="number"
                min={0}
                value={declaredValue}
                onChange={(e) => setDeclaredValue(e.target.value)}
                error={fieldErrors.declared_value_cents}
                hint="Required if you want cover for loss or damage."
              />
            </div>
            <label className="mt-4 flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={insured}
                onChange={(e) => setInsured(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand"
              />
              <span>
                Add insurance cover
                <span className="block text-xs text-ink-400">Charged as a percentage of declared value and shown in the rates.</span>
              </span>
            </label>
          </Card>

          {hasProhibited && (
            <p className="text-sm text-ink-400">
              Remove or change the flagged item above before continuing -- we can't ship it by courier.
            </p>
          )}

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(0)}>
              Back
            </Button>
            <Button disabled={hasProhibited} loading={quoteMutation.isPending} onClick={() => quoteMutation.mutate()}>
              Get rates
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <div className="space-y-5">
          {/* What the price is actually for. A rate presented without its
              assumptions is a rate somebody books and then disputes, and the
              parcel fields start on a 1 kg small box that is easy to leave
              unchanged. */}
          <Card className="text-sm text-ink-600">
            Priced for{" "}
            <span className="font-semibold">
              {parcels.length === 1 ? "1 parcel" : `${parcels.length} parcels`},{" "}
              {(totalWeight / 1000).toFixed(2)} kg
            </span>
            {parcels.length === 1 && (
              <>
                {" "}
                at {parcels[0].length_mm} × {parcels[0].width_mm} × {parcels[0].height_mm} mm
              </>
            )}
            .{" "}
            <button type="button" className="font-medium text-brand underline" onClick={() => setStep(1)}>
              Change the parcel
            </button>{" "}
            if that is not what you are sending.
          </Card>

          {quoteMutation.isPending ? (
            <Card className="flex items-center gap-3 text-sm text-ink-400">
              <Spinner /> Working out your options
            </Card>
          ) : !quote?.options.length ? (
            // The server says which it is -- nothing we can carry, or nothing
            // left to collect today -- and sending someone to re-check their
            // addresses over a passed cutoff wastes their time.
            <Alert tone="warn">
              {message || "We cannot ship this consignment. Check the addresses and parcel weights, or contact us for a manual quote."}
            </Alert>
          ) : (
            <div className="grid gap-3">
              {quote.options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelected(option)}
                  className={cx(
                    "ws-card flex flex-wrap items-center gap-4 p-5 text-left transition",
                    selected?.id === option.id ? "border-brand ring-2 ring-brand/20" : "hover:border-ink-300",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{option.service_name}</p>
                      {option.badge && (
                        <Badge tone={option.badge === "cheapest" ? "good" : option.badge === "fastest" ? "progress" : "neutral"}>
                          {option.badge}
                        </Badge>
                      )}
                    </div>
                    {option.service_note && <p className="mt-0.5 text-sm text-ink-500">{option.service_note}</p>}
                    <p className="mt-1 text-sm text-ink-400">
                      {option.earliest_collection_date
                        ? `Collected ${formatDate(option.earliest_collection_date)}`
                        : transitCopy(option.transit_days)}
                      {formatDeliveryEstimate(option) ? `, delivered ${formatDeliveryEstimate(option)}` : ""}
                    </p>
                    {option.collection_cutoff && (
                      <p className="mt-0.5 text-xs text-ink-400">Collection cut-off {option.collection_cutoff}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold">{money(option.price_cents)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button disabled={!selected} onClick={() => setStep(3)}>
              Review booking
            </Button>
          </div>
          </div>

          <BookingSummary
            collection={collection}
            delivery={delivery}
            parcels={parcels}
            selected={selected}
            wallet={wallet}
          />
        </div>
      )}

      {step === 3 && selected && (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <div className="space-y-5">
          <Card>
            <h2 className="mb-4 text-lg font-semibold tracking-tight">Review</h2>
            <dl className="grid gap-4 sm:grid-cols-2">
              <Summary label="Collect from" value={`${collection.street}, ${collection.suburb}, ${collection.city}`} />
              <Summary label="Deliver to" value={`${delivery.street}, ${delivery.suburb}, ${delivery.city}`} />
              <Summary label="Service" value={selected.service_name} />
              <Summary label="Transit" value={transitCopy(selected.transit_days)} />
              <Summary label="Parcels" value={`${parcels.length}, ${(totalWeight / 1000).toFixed(2)} kg`} />
              <Summary label="Reference" value={reference || "None"} />
            </dl>
          </Card>

          <Card>
            <h2 className="mb-1 text-lg font-semibold tracking-tight">When</h2>
            <p className="mb-4 text-sm text-ink-400">
              {selected.collection_cutoff
                ? `${selected.service_name} collects by ${selected.collection_cutoff}.`
                : `What ${selected.service_name} can collect and deliver.`}
            </p>
            <SchedulePicker option={selected} value={schedule} onChange={setSchedule} errors={fieldErrors} />
          </Card>

          <Card>
            <h2 className="mb-1 text-lg font-semibold tracking-tight">Who is receiving it</h2>
            <p className="mb-4 text-sm text-ink-400">
              The driver calls this person on arrival. Not needed to price a shipment, which is why
              it is asked for here rather than before the rates.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Recipient name"
                name="delivery-contact"
                value={delivery.contact_name}
                error={fieldErrors["delivery.contact_name"]}
                onChange={(event) => setDelivery({ ...delivery, contact_name: event.target.value })}
              />
              <Field
                label="Recipient phone"
                name="delivery-phone"
                inputMode="tel"
                value={delivery.phone}
                error={fieldErrors["delivery.phone"]}
                onChange={(event) => setDelivery({ ...delivery, phone: event.target.value })}
              />
              <Field
                label="Recipient email (optional)"
                name="delivery-email"
                type="email"
                value={delivery.email ?? ""}
                onChange={(event) => setDelivery({ ...delivery, email: event.target.value })}
                hint="Sends them the tracking link."
                className="sm:col-span-2"
              />
            </div>
          </Card>

          </div>

          {/* The button goes under the figure it is committing to, inside the
              summary, rather than alone in a card of its own: once the money
              moved into this panel, an action bar beside it had nothing left
              to sit next to and floated in the middle of the page. */}
          <BookingSummary
            collection={collection}
            delivery={delivery}
            parcels={parcels}
            selected={selected}
            wallet={wallet}
            action={
              <>
                {!canBook && <p className="mb-2 text-sm text-ink-500">{summariseMissing(bookingProblems)}</p>}
                <Button
                  className="w-full"
                  loading={bookMutation.isPending}
                  onClick={() => guard(bookingProblems, () => bookMutation.mutate())}
                >
                  {payingByCard ? `Pay ${money(dueNow)} and book` : "Confirm and book"}
                </Button>
                {payingByCard && (
                  <p className="mt-2 text-xs text-ink-500">
                    We will take you to our payment page. Your booking is held until it clears.
                  </p>
                )}
              </>
            }
          />

          {/* Last in the grid, so on a narrow screen it falls below the
              summary: the way back should not come before the way forward. */}
          <Button variant="secondary" className="justify-self-start lg:col-start-1" onClick={() => setStep(2)}>
            Back to rates
          </Button>
        </div>
      )}
    </>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-400">{label}</dt>
      <dd className="mt-1 text-sm">{value}</dd>
    </div>
  );
}
